import React, { useCallback, useState, useEffect, useRef } from 'react';
import { Ajax } from '../../components/Ajax';
import { message } from 'antd';
import { useDispatch } from 'react-redux';
import { closePopup, updatePopupTitle } from './../../store/actions';
import { useDelete, useCustomEvent } from '../../project/utilities';
import { validate } from '../Form/validation';
import { useHistory } from 'react-router-dom';
import { showLoader, hideLoader } from '../Loader';
import uuidv4 from 'uuid/v4';
import { T } from '../../components/Translations';

/**
 * custom hook used in detail components to provide the necessary methods
 * @param props contains data, updateUrl, dataLoadUrl, onLoadData, windowKey, nameField, deleteUrl, deleteKeys, backPage, additionalData, listUpdateEvent, validationFields, detailUrl
 * @exports useDetail
 */
export default function useDetail({ data,
    updateUrl,
    updateLocally,
    dataLoadUrl,
    dataLoadParams,
    onLoadData,
    windowKey,
    nameField,
    deleteUrl,
    deleteKeys,
    backPage,
    additionalData,
    listUpdateEvent,
    validationFields,
    changingValidation,
    skipInitialValidation,
    detailUrl,
    reloadEvent,
    detailProps,
    externalValidation }) {
    const dispatch = useDispatch();
    const [dataItem, setDataItem] = useState(null);
    const [validation, setValidation] = useState({});
    const [allowValidation, setAllowValidation] = useState(true);
    const [updating, setUpdating] = useState(false);
    const [loading, setLoading] = useState(false);
    const detailRef = useRef(false);
    const history = useHistory();
    const [loaderName] = useState(uuidv4());
    const loadData = useCallback((id) => {
        if (loading) return;
        setLoading(true);
        Ajax.get({
            url: dataLoadUrl,
            data: dataLoadParams || { id: id || data.ID || "" },
            success: function (response) {
                detailRef.current && (
                    onLoadData?.(response, setDataItem) || 
                    // setDataItem(response)
                    setDataItem(prev => ({ ...prev, ...response }))
                );
                setLoading(false);
            },
            error: function () {
                setLoading(false);
            }
        });
        return true;
    }, [dataLoadUrl, onLoadData, data.ID, detailRef, loading]);// eslint-disable-line react-hooks/exhaustive-deps

    const setReload = useCallback(() => { dataItem && setDataItem({ ...dataItem, reloadData: true }); }, [dataItem]);
    useCustomEvent(reloadEvent, setReload);

    useEffect(() => {
        return () => {
            hideLoader(loaderName);
        };
    }, []);// eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        updating || loading ? showLoader(loaderName) : hideLoader(loaderName);
    }, [updating, loading, loaderName]);

    useEffect(() => {
        detailRef.current = true;
        setAllowValidation(validationFields && !skipInitialValidation);
        return () => { detailRef.current = false; };
    }, []);// eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (detailRef.current && dataItem && allowValidation) {
            setValidation(validate({ data: dataItem, validationFields }));
        }
    }, [dataItem, changingValidation && validationFields]);// eslint-disable-line react-hooks/exhaustive-deps


    useEffect(() => {
        if (detailRef.current) {
            (dataLoadUrl && loadData()) ||
                setDataItem(data);
        }
    }, [data.ID]);// eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (detailRef.current) {
            if (dataItem?.reloadData) {
                loadData(dataItem.ID);
            }
        }
        // eslint-disable-next-line
    }, [dataItem?.reloadData]);

    const closeDetailPopup = useCallback(() => {
        dispatch(closePopup(windowKey));
    }, [windowKey, dispatch]);

    const updateData = useCallback((successCallback, errorCallback, options = {}) => {
        if (updating) return;
        setUpdating(true);
        let validationData = { isValid: true };
        if (!options.skipValidation && validationFields) {
            validationData = validate({ data: dataItem, validationFields });
            setValidation(validationData);
            setAllowValidation(true);
        }
        if (validationData.isValid && (!externalValidation || externalValidation.isValid)) {
            if (updateLocally) {

                setUpdating(false);
                if(successCallback) { successCallback(); }
            } else {
                Ajax.post({
                    url: updateUrl,
                    data: options.overrideData || { ...additionalData, ...dataItem, ...options.additionalData },
                    success: function (response) {
                        setUpdating(false);
                        if (response) {
                            if (response.HasError) {
                                message.error(response.Message);
                                if(errorCallback) { errorCallback(response); }
                            }
                            else {
                                message.success(response.Message);
                                windowKey && detailProps?.editTitle && dispatch(updatePopupTitle({ windowKey, title: detailProps.editTitle }));
                                if(successCallback) { successCallback(response); }
                                if (detailUrl && response.ID !== dataItem.ID) {
                                    history.push(detailUrl + "/" + response.ID);
                                }
                            }
                            if (allowValidation && detailRef.current) { setAllowValidation(validationFields && !skipInitialValidation); }
                        }
                    },
                    error: function (response) {
                        setUpdating(false);
                        message.error(response.message || response.title);
                        if(errorCallback) { errorCallback(response); }
                    }
                });
            }
        } else {
            message.error(<T>message.fill_in_required_fields</T>);
            setUpdating(false);
            if(errorCallback) { errorCallback(); }
        }
    }, [dataItem, updateUrl, validationFields, allowValidation, detailUrl, updating, setUpdating, detailProps]);// eslint-disable-line react-hooks/exhaustive-deps

    const onChangeData = useCallback((event, callback) => {
        let newData = { ...dataItem };
        let deepObject = newData;
        let name = event.target.name;
        const regex = /\.|\[|\]/;
        if (name && name.search(regex) !== -1) {
            let deepProps = name.split(regex).filter(p => p !== '');
            for (let i = 0; i < deepProps.length - 1; i++) {
                deepObject[deepProps[i]] = Array.isArray(deepObject[deepProps[i]]) ? [...deepObject[deepProps[i]]] : { ...deepObject[deepProps[i]] };
                deepObject = deepObject[deepProps[i]];
            }
            name = deepProps[deepProps.length - 1];
        }
        if (event.target && name) {
            switch (event.target.type) {
                case 'checkbox':
                    deepObject[name] = event.target.checked;
                    break;
                default:
                    deepObject[name] = event.target.value;
                    break;
            }
        }
        if(callback) { callback(event, newData); }
        setDataItem(newData);
    }, [dataItem]);

    const onDetailDelete = useDelete({
        nameField,
        deleteUrl,
        backPage,
        listUpdateEvent,
        deleteKeys
    });

    return { isNew: dataItem && !Boolean(dataItem.ID), dataItem, setDataItem, updateData, onChangeData, closePopup: closeDetailPopup, onDetailDelete, detailRef, validation, updating };
}